/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the TermListItem component that is displayed at the top of each
 * view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI ListItem component.
 */
import ListItem from '@material-ui/core/ListItem';

/**
 * Material UI ListItemIcon component.
 */
import ListItemIcon from '@material-ui/core/ListItemIcon';

/**
 * Material UI ListItemText component.
 */
import ListItemText from '@material-ui/core/ListItemText';

/**
 * Material UI Fade component.
 */
import Fade from '@material-ui/core/Fade';

/**
 * Material UI Divider component.
 */
import Divider from '@material-ui/core/Divider';

/**
 * Material UI Grid component.
 */
import Grid from '@material-ui/core/Grid';

/**
 * Material UI Typography component.
 */
import { Typography } from '@material-ui/core';

/**
 * Used to display SVG icons.
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/**
 * SVG icon definition
 */
import { faFileMedical } from '@fortawesome/pro-solid-svg-icons/faFileMedical';

/**
 * SVG icon definition
 */
import { faFileMedicalAlt } from '@fortawesome/pro-solid-svg-icons/faFileMedicalAlt';

/**
 * Material UI Link component.
 */
import Link from '@material-ui/core/Link';

/**
 * used to navigate around the app.
 */
import { Link as RouterLink } from 'react-router-dom';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of term data loaded from the ServiceStack API.
 */
import useTermsIndividual from 'src/hooks/useTermsIndividual';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

/**
 * changes \n's to <br />'s
 */
import convertReturnsToHtml from 'src/utils/convertReturnsToHtml';

/**
 * Enables the use of global app state.
 */
import { useAppSelector, AppSelectors } from 'src/store/reducers/app';

/**
 * Used to highlight search terms in text.
 */
import SearchHighlighter from './SearchHighlighter';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface ITermListItemProps {
    termId: number;
    showDivider?: boolean;
    version: Dtos.Version;
    category: Dtos.Category;
    parentTerm?: Dtos.Term | null;
    getTerm?: (categoryId: number | null, termId: number, subtermId: number | null) => [Dtos.Term | null];
    search?: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    listItem: {
    },
}));

const useGridStyles = makeStyles(theme => ({
    gridRow: {
        width: 'auto',
        padding: theme.spacing(1, 0),
        borderBottom: `1px solid rgba(0, 0, 0, 0.12)`,
        // marginRight: -theme.spacing(2),

        '&:first-child': {
            paddingTop: 0
        },

        '&:last-child': {
            borderBottom: 'none'
        }
    },
    gridTitle: {

    },
    gridValue: {
        alignSelf: 'center'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const GradeListItemGridRow: React.FunctionComponent<{label: string; value: React.ReactNode; }> = ({
    label,
    value
}) => {
    const classes = useGridStyles();

    return (
        <Grid
            container
            className={classes.gridRow}
        >
            <Grid
                item
                className={classes.gridTitle}
                xs={12}
                sm={2}
                md={2}
                lg={2}
                xl={1}
            >
                <Typography
                    variant="h6"
                >
                    {label}
                </Typography>
            </Grid>
            <Grid
                item
                xs={12}
                sm={10}
                md={10}
                lg={10}
                xl={11}
                className={classes.gridValue}
            >
                {value}
            </Grid>
        </Grid>
    )
}

const TermListItem: React.FunctionComponent<ITermListItemProps> = ({
    termId,
    version,
    category,
    parentTerm,
    showDivider,
    getTerm,
    search
}) => {
    // Get classnames for styles.
    const classes = useStyles();

    const [term] = getTerm ?
        getTerm(category.id, parentTerm ? parentTerm.id : termId, parentTerm ? termId : null) :
        useTermsIndividual(category.id, parentTerm ? parentTerm.id : termId, parentTerm ? termId : null);

    const targetTermId = useAppSelector(AppSelectors.selectTargetTermId);

    return (
        <Fade
            in={true}
        >
            <div>
                <ListItem
                    button
                    className={classes.listItem}
                    component={RouterLink}
                    to={`/${version.code}/browse/term/${termId}`}
                >
                    <ListItemIcon>
                        <FontAwesomeIcon fixedWidth icon={parentTerm ? faFileMedicalAlt : faFileMedical} size="lg" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            term ?
                                search ?
                                    <>
                                        <GradeListItemGridRow
                                            label="Category"
                                            value={
                                                targetTermId ?
                                                    <Typography>
                                                        <SearchHighlighter
                                                            searchTerms={search}
                                                        >
                                                            {category.name}
                                                        </SearchHighlighter>
                                                    </Typography>:
                                                    <Link
                                                        component={RouterLink}
                                                        to={`/${version.code}/browse/category/${category.id}`}
                                                    >
                                                        <SearchHighlighter
                                                            searchTerms={search}
                                                        >
                                                            {category.name}
                                                        </SearchHighlighter>
                                                    </Link>
                                            }
                                        />
                                        {
                                            parentTerm ?
                                                <GradeListItemGridRow
                                                    label="Term"
                                                    value={
                                                        targetTermId && targetTermId !== parentTerm.id ?
                                                            <Typography>
                                                                <SearchHighlighter
                                                                    searchTerms={search}
                                                                >
                                                                    {parentTerm.name}
                                                                </SearchHighlighter>
                                                            </Typography> :
                                                            <Link
                                                                component={RouterLink}
                                                                to={`/${version.code}/browse/term/${parentTerm.id}`}
                                                            >
                                                                <SearchHighlighter
                                                                    searchTerms={search}
                                                                >
                                                                    {parentTerm.name}
                                                                </SearchHighlighter>
                                                            </Link>
                                                    }
                                                /> :
                                                null
                                        }
                                        <GradeListItemGridRow
                                            label={
                                                parentTerm ?
                                                    'Subterm' :
                                                    'Term'
                                            }
                                            value={
                                                <Typography>
                                                    <SearchHighlighter
                                                        searchTerms={search}
                                                    >
                                                        {term.name}
                                                    </SearchHighlighter>
                                                </Typography>
                                            }
                                        />
                                    </> :
                                    term.name :
                                ''
                        }
                        secondary={
                            term ?
                                search ?
                                    <SearchHighlighter
                                        searchTerms={search}
                                        convertReturns
                                    >
                                        {term.description}
                                    </SearchHighlighter> :
                                    convertReturnsToHtml(term.description) :
                                ''}
                    />
                </ListItem>
                {
                    showDivider && (
                        <Divider />
                    )
                }
            </div>
        </Fade>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Export - Default
 * ---------------------------------------------------------------------------------
 */

export default TermListItem;


// WEBPACK FOOTER //
// ./src/components/TermListItem.tsx